/**
 * Created by Ning Jiang on 11/23/2016.
 */

import {IGameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/IGameConfig";
import {IReelSets} from "@nolimitcity/slot-game/bin/core/reel/reelset/IReelSets";
import {IDeviceDependentConfig} from "@nolimitcity/slot-game/bin/core/useragent/UserAgent";

import {IWinRatios, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {WinterHoardGameAssets} from "./WinterHoardGameAssets";

import {LayerScaleOption} from "@nolimitcity/slot-game/bin/core/stage/LayerScaleOption";
import {Back, Ease} from "gsap";
import {IWinterHoardSymbolStates} from "./symbols/WinterHoardReelSymbol";
import {IWinterHoardLayerConfig, WinterHoardLayerConfig} from "./config/WinterHoardLayerConfig";
import {WinterHoardSoundConfig} from "./WinterHoardSoundConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {SpinMode} from "@nolimitcity/slot-game/bin/core/gametype/SpinMode";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";



export interface IBuffaloWinRatioConfig {
    readonly ratio:number;
    readonly text?:string;
    readonly duration:number; // In second.
    readonly type?:WinRatioType;
    readonly sound:string;
}

export interface IBuffaloWinRatios extends IWinRatios{
    readonly normalWin:IBuffaloWinRatioConfig[];
    readonly bigWin:IBuffaloWinRatioConfig[];
}

export class WinterHoardGameConfig implements IGameConfig {

    public static instance:WinterHoardGameConfig = new WinterHoardGameConfig();

    BROWSER_FPS:number = 30;

    GAME_WIDTH:number = 1280;
    GAME_HEIGHT:number = 720;

    DEFAULT_LAYER_SCALE_OPTION:IDeviceDependentConfig<LayerScaleOption> = WinterHoardLayerConfig.defaultLayerScaleOptions;

    LAYERS:IWinterHoardLayerConfig = WinterHoardLayerConfig.getConfig();

    BET_WIN_MODE:BetWinMode = BetWinMode.BET_LINE;
    SPIN_MODE:SpinMode = SpinMode.SPIN;

    REEL_AREA_POS_X:number = 0;
    REEL_AREA_POS_Y:number = 90;
    REEL_AREA_WIDTH:number = 1000;
    REEL_AREA_HEIGHT:number = 720;
    REEL_AREA_MASK:boolean = true;
    REEL_AREA_MASK_RECT:PIXI.Rectangle = new PIXI.Rectangle(0, 66, 870, 650);

    SYMBOL_STATES:IWinterHoardSymbolStates = {
        normal : {
            prio : 0,
            keyword : "normal",
            texturesCreator : (symName:string, stateKey:string) => {
                if(["L1","L2","L3","L4","L5","M1","M2","M3","M4","M5"].indexOf(symName) > -1){
                    return [new PIXI.Sprite().texture];
                }
                if(["S"].indexOf(symName) > -1){
                    return GameResources.getTextures("scatterLanding");
                }

                if(["W"].indexOf(symName) > -1){
                    return GameResources.getTextures("wildLanding");
                }
                return WinterHoardGameAssets.getSymbolStateTextures(symName);
            }
        },
        spin : {
            prio : 1,
            keyword : "spin",
            texturesCreator : (symName:string, stateKey:string) => {
                return WinterHoardGameAssets.getSymbolStateTextures(symName, stateKey);
            }
        },
        trigger: {
            prio : 2,
            keyword : "trigger",
            symbols : ["S"],
            texturesCreator : (symName:string, stateKey:string) => {
                return GameResources.getTextures("scatterLanding");
            },
            fpsGetter : (symName, stateKey) => 15,
            offsetGetter: (symName, stateKey) => [4, 0]
        },
        plus: {
            prio : 3,
            keyword : "plus",
            symbols : ["S"],
            texturesCreator : (symName:string, stateKey:string) => {
                return GameResources.getTextures("scatterNearWin");
            },
            fpsGetter : (symName, stateKey) => 15,
            offsetGetter: (symName, stateKey) => [4, 0]
        },
        landing: {
            prio : 4,
            keyword : "landing",
            symbols : ["S"],
            texturesCreator : (symName:string, stateKey:string) => {
                return GameResources.getTextures("scatterLanding");
            },
            fpsGetter : (symName, stateKey) => 15,
            offsetGetter: (symName, stateKey) => [4, 0]
        },
        idle : {
            prio : 5,
            keyword : "idle",
            symbols : ["W"],
            texturesCreator : (symName:string, stateKey:string) => {
                return GameResources.getTextures("wildIdle");
            },
            fpsGetter : (symName, stateKey) => 15,
            offsetGetter: (symName, stateKey) => [4, 0]
        },
        noWin : {
            prio : 6,
            keyword : "noWin",
            texturesCreator : (symName:string, stateKey:string) => {
                if(["L1","L2","L3","L4","L5","M1", "M2","M3","M4","M5"].indexOf(symName) > -1){
                    return [new PIXI.Sprite().texture];
                }
                return WinterHoardGameAssets.getSymbolStateTextures(symName);
            }
        },
        win : {
            prio : 7,
            keyword : "win",
            texturesCreator : (symName:string, stateKey:string) => {
                if(["L1","L2","L3","L4","L5","M1", "M2","M3","M4","M5"].indexOf(symName) > -1){
                    return [new PIXI.Sprite().texture];
                }
                return WinterHoardGameAssets.getSymbolStateTextures(symName);
            }
        },
        energy : {
            prio : 8,
            keyword : "energy",
            texturesCreator : (symName:string, stateKey:string) => {
                return WinterHoardGameAssets.getSymbolStateTextures(symName);
            }
        },
        collect : {
            prio : 9,
            keyword : "collect",
            texturesCreator : (symName:string, stateKey:string) => {
                return WinterHoardGameAssets.getSymbolStateTextures(symName);
            }
        },
        hidden : {
            prio : 10,
            keyword : "hidden"
        }
    };
    SYMBOL_WIDTH:number = 144;
    SYMBOL_HEIGHT:number = 155;
    SYMBOL_BOTTOM_PADDING:number = 0;

    /*REELS_NUM:number = 5;
    SPACE_BETWEEN_REELS:number = 0;
    REEL_DISPLAY_POSITIONS:[number, number][] = [
        [this.SYMBOL_WIDTH * 0.5, 0],
        [this.SYMBOL_WIDTH * 1.5 + this.SPACE_BETWEEN_REELS, 0],
        [this.SYMBOL_WIDTH * 2.5 + this.SPACE_BETWEEN_REELS * 2, 0],
        [this.SYMBOL_WIDTH * 3.5 + this.SPACE_BETWEEN_REELS * 3, 0],
        [this.SYMBOL_WIDTH * 4.5 + this.SPACE_BETWEEN_REELS * 4, 0]];*
     */
    REELS_NUM:number = 5;
    SPACE_BETWEEN_REELS:number = 10;
    REEL_XPOS_OFFSET:number = 9;
    REEL_DISPLAY_POSITIONS:[number, number][] = [
        [this.REEL_XPOS_OFFSET + (this.SYMBOL_WIDTH * 0.5), -5],
        [this.REEL_XPOS_OFFSET + (this.SYMBOL_WIDTH * 1.53 + this.SPACE_BETWEEN_REELS), -5],
        [this.REEL_XPOS_OFFSET + (this.SYMBOL_WIDTH * 2.62 + this.SPACE_BETWEEN_REELS), -5],
        [this.REEL_XPOS_OFFSET + (this.SYMBOL_WIDTH * 3.7 + this.SPACE_BETWEEN_REELS), -5],
        [this.REEL_XPOS_OFFSET + (this.SYMBOL_WIDTH * 4.77 + this.SPACE_BETWEEN_REELS), -5]];
    SYMBOLS_NUM_IN_REEL:[number, number, number, number, number] = [1, 1, 4, 1, 1];


    REEL_SPIN_TIME:number = 0.9;
    REEL_FAST_SPIN_SPIN_TIME:number = 0.2;

    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DURATION:number = 0.2;
    REEL_SPIN_START_SYMBOL_CHANGE_TO_SPIN_STATE_DELAY:number = 0.3;
    REEL_SPIN_START_EASE:Ease = Back.easeIn.config(4);
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DURATION:number = 0.2;
    REEL_SPIN_STOP_SYMBOL_CHANGE_FROM_SPIN_STATE_DELAY:number = 0;
    REEL_SPIN_STOP_ELASTIC_CONFIG:[number, number] = [1.5, 0.95];
    REEL_SPIN_QUICK_STOP_ELASTIC_CONFIG:[number, number] = [1.2, 0.75];

    REEL_SPIN_START_SPEED:number = 0.5;
    REEL_SPIN_SPIN_SPEED:number = 0.05;
    REEL_NEAR_WIN_SPIN_SPEED:number = 0.03;

    REEL_SPIN_STOP_SPEED:number = 0.5;
    REEL_SPIN_QUICK_STOP_SPEED:number = 0.3;

    REEL_STOP_DELAY:number = 0.3;
    REEL_QUICK_STOP_DELAY:number = 0.05;
    REEL_NEAR_WIN_STOP_DELAY:number = 3.0;

    REEL_SETS:IReelSets = {
        "BASE_REELSET" :
            [
                ["L3","L3","L2","L4","L3","L3","M2","M2","M2","L2","L1","L1","L2","L5","L5","M3","M4","M5","L1","L2",
                "L2","L1","M5","M5","S","M5","M4","M5","M4","M3","L3","L5","L3","L4","L5","L3","L4","M4","L3","M3",
                "M4","M5","M4","M5","L3","L5","L3","L4","L3","L5","L1","L2","L1","L2","M1","M1","M1","M1","M2","M3",
                "M5","W","L4","L5","L4","L5","L4","L2","S","L1","L2","L1","L5","L3","L4","L5","L3","L4","L4","L3",
                "L2","L3","L3","L5","L5","M2","M2","L2","L2","S","L2","L2","L5","M3","M3","M5","L1","L1","L2","L1",
                "M5","M3","M5","M4","M5","M4","M3","L3","L5","L3","L4","L5","L3","L4","M3","M3","L4","M4","M5","M4",
                "M5","L3","L5","L3","L4","L3","L5","L1","L2","L1","L2","M1","M1","M1","M1","M2","M3","M5","L3","L4",
                "L5","L4","L5","L4","L2","L1","L2","L1","L5","L3","L4","L5","L3","L4"],

                ["M3","M5","M3","M4","M3","L2","L1","L2","L1","M5","M5","L3","L3","L4","L3","L4","L3","L5","L4","L5",
                    "L4","L5","L1","L3","L2","L1","L2","L1","M1","M1","M1","M1","L2","L1","L2","L1","L3","L4","L5","L5",
                    "L2","L1","M4","M4","M5","S","M4","L1","L2","L1","L2","L2","L1","M2","M2","S","M2","M4","L5","L4",
                    "L5","L3","L1","L2","L1","L5","W","L4","L3","L5","L3","L2","L5","L3","L5","L3","M3","M5","M3","M5",
                    "M3","M4","M3","L2","L1","L2","L1","M5","M5","L3","L3","L4","L3","L4","L3","L5","L4","L5","L4","L5",
                    "L1","L3","L2","L1","L2","L1","M1","M1","M1","M1","L2","L1","L2","L1","L3","L4","L5","L5","L2","L1",
                    "M4","M4","M5","S","M4","L1","L2","L1","L2","L2","L1","M2","M2","S","M2","M4","L5","L4","L5","L3",
                    "L1","L2","L1","L5","L5","L4","L3","L5","L3","L2","L5","L3","L5","L3","M3","M5"],

                ["M3","M4","M5","M2","M3","M4","L5","L3","L1","L4","L2","S","L2","L3","L2","M4","M3","M4","M5","L5",
                    "L3","L1","L3","M4","M5","M3","M4","L4","W","L2","L1","L5","M1","M1","M1","M1","M3","L3","L4","M5",
                    "M2","M3","M4","M5","L4","L3","L4","L1","L2","W","M3","M2","M5","L4","L1","L2","L5","L3","L2","L4",
                    "L5","L2","L4","S","L3","L2","L5","L3","L4","L5","L3","L4","L1"],

                ["L3","L4","W","L3","L1","L2","L1","W","L2","M4","M3","L3","L5","L3","L1","M2","M5","M4","M2","L5",
                    "L2","W","L4","L5","L1","L2","L5","M1","M1","M1","M1","L4","L5","L1","L2","L5","M4","M2","M3","M5",
                    "M3","M2","L2","L1","S","L2","M3","M2","M5","M2","L5","L4","L5","L4","W","M2","M3","M5","M2","M3",
                    "M2","S","M5","M2","L1","L2","L1","L3","L5","L3","L4"],

                ["L4","M5","M3","M2","L4","W","L3","L4","M4","M5","M2","M4","M5","L1","L2","M2","M5","L3","W","L1",
                    "L2","L5","L1","L2","L5","M4","M3","S","M4","M3","L1","L4","L5","L3","M1","M1","M1","M1","L2","L2",
                    "L1","L4","M2","L1","L3","M5","L5","L4","L2","L5","M3","M4","L2","M5","M4","L4","L5","S","L4","M5",
                    "M3","M2","L5","L4","L3","L5","L4","W","L5","L4","L3","L5","W","L2","L1","L3","L3","L5"]
        ],

        "FREESPIN_REELSET" : [ // TODO_UNUSED
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "M3", "L3", "M2", "L2", "M4", "L5", "W", "L4", "M5", "S"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L3", "M2", "L2", "M4", "L5", "L5", "M3", "W", "L4", "M5", "S"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L3", "M2", "L2", "M4", "L5", "L5", "M3", "W", "L4", "M5", "S"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5", "S"],
            ["M1", "L1", "L3", "M2", "L2", "M4", "L5", "L3", "M2", "L2", "M4", "L5", "M3", "W", "L4", "M5", "S"]
        ],

        "FREESPIN_TRANSFORM" : [
            ["L3","L2","L1","L3","L5","L1","L3","M2","L2","L1","L5","L3","L1","L2","L4","L1","L3","L5","L1","L3",
                "M5","L1","L5","L3","M4","L5","L1","L3","L5","L2","M1","M1","M1","M1","L3","L5","L1","L3","L4","L5",
                "L3","L1","L4","L3","M3","L1","L3","L4","L1","M5","L2","L5","L1","L3","M1","M1","M1","M1","M4","L4",
                "L1","L3","L5","L4","L3","L1","L4","L3","L1","W","L4","L3","L1","L5","L4","L1","L5","L3","M3","M4",
                "L1","L5","L4","L2","L1","M4","L4","L3","M2","L1","L4","L3","L1","M5","L3","L4","L1","M5","M1","M1",
                "M1","M1","L4","L5","L1","L3","L2","L1","L3","W","M5","L5","L3","M4","L5","W","L2","L1","L3","L5",
                "L2","L3","M1","M1","M1","M1","L4","L1","L3","L5","L4","L1","L3","M3","L1","L4","M5","L3","L1","L4",
                "L3","L1","L5","L4","L3","L1","M5","L3","W","L5","L3","L2","L1","L3","L5","L1","L4","L3","L5","L1",
                "M5","M3","L4","L5","L3","L1","L5","L3","L1","L5","L4","M5","L3","L2","L5","L4","L1","M5","L3","L1",
                "M3","L3","L1","L2","L3","L5","M1","M1","M1","M1","L2","L3","L4","L5","L3","M5","L4","L3","L1","L2",
                "M2","L5","L3","L1","L2","L3","M2","L2","L1","L3","M2","L5","L4","L1","L3","L5","M5","L1","L3","L5"],

            ["L2","L5","M5","L2","L4","L5","L2","M3","L4","M2","L2","L4","M5","L3","L2","L5","L1","L2","L4","W",
                "L1","L3","L5","M5","L2","L4","L3","M2","M3","L2","M1","M1","M1","M1","L4","L3","M3","L2","L1","L4",
                "M2","L1","L3","L2","L5","L4","L2","M3","L4","L2","L3","L4","L1","W","L4","M5","L2","L5","L4","L2",
                "L5","L4","L2","M5","L4","W","L3","L2","L4","M5","L2","L4","L1","L2","L4","L3","M3","L3","L2","L3",
                "L4","L2","L5","M5","M1","M1","M1","M1","L2","L5","L4","M5","M4","L1","L2","L4","L1","L4","L4","L2",
                "L1","L5","W","L2","M3","L5","L4","L3","M1","M1","M1","M1","M3","L5","L4","L2","M5","L3","M4","L2",
                "L5","W","L2","L3","L4","M5","L2","L4","L1","M2","M5","L4","M1","M1","M1","M1","M3","L5","L4","W",
                "L2","M2","L4","M5","L2","L1","M2","L2","W","L5","M3","M5","L2","L1","L4","M5","L2","L3","L4","W",
                "L5","M4","L2","M5","L4","L2","L3","L4","L2","M3","L4","L1","L2","L4","M5","L2","M3","L5","L4","M5",
                "L2","L1","L4","L2","M5","L4","M1","M1","M1","M1","M4","L1","L2","L4","M5","M3","L4","M5","W","L4",
                "L1","L2","M5","L4","L2","M3","L5","M5","L3","L1","M1","M1","M1","M1","L4","M5","M4","L4","L2","L5",
                "M4","L2","L3","M5"],

            ["L2","M4","M5","L2","L3","L5","L4","L1","L2","L3","L5","L1","M4","L4","M5","W","L3","L4","M5","L1",
                "M1","M1","M1","M1","L4","L3","L2","M5","L5","L1","L2","L4","M2","M5","L1","W","L3","L4","L2","M5",
                "M4","L3","L4","L2","L1","L5","L4","L1","L3","L4","L1","L3","L2","L1","M1","M1","M1","M1","L4","L2",
                "L3","M5","L4","L5","M4","L3","L4","L5","M1","M1","M1","M1","L1","M2","L2","L4","M5","L3","L4","L5",
                "L3","M5","L2","L4","L1","L2","L3","W","L4","L3","W","L4","L3","L5","L2","W","M4","L5","L4","M2",
                "M5","L2","L1","L3","M4","L2","L4","L3","L5","M2","L2","L1","L3","L5","M5","L3","L4","M4","W","L4",
                "L1","L3","L2","L1","L4","M5","L5","L3","L4","L1","L3","L2","L4","M4","L1","L2","W","L1","L3","L2",
                "L1","L5","M1","M1","M1","M1","M5","L1","L2","L3","L1","W","M5","L5","L3","L1","L2","W","M2","L1",
                "M3","L3","L4","L5","L3","L1","L2","L3","M1","M1","M1","M1","M2","M4","L5","L1","L3","L4","M4","L1",
                "M5","L2","L5","M5","L4","L1","L5","M4","M2","L1","L4","L5","M5","M4","L3","M5","L5","L3","W","L2",
                "L4","M5","L2","L1","M5","L4","L2","M4","L4","L1","L2","L5","M1","M1","M1","M1","L4","M4","L2","L1",
                "L5","L2","M5","L5","L1","L4"],

            ["M4","L4","L5","W","L4","L1","L3","L2","M3","L1","L4","L3","L2","L5","L3","L1","L2","M3","M5","L1",
                "M1","M1","M1","M1","L3","L4","M4","L2","W","L4","L2","L1","L4","L2","L3","M3","L1","L4","L3","L2",
                "L1","L4","L5","L2","W","M5","L3","M4","L1","L3","L2","M3","L3","M5","L4","M1","M1","M1","M1","L3",
                "L2","L4","M5","L5","L3","L2","M5","L3","L4","L2","M4","L4","L2","L5","L3","W","L4","L1","L3","M5",
                "L2","M4","L3","M5","M4","L3","L4","M3","L5","L3","M1","M1","M1","M1","L1","L2","M4","L5","L4","L3",
                "L1","M4","L3","M5","M4","L4","L3","M5","L4","L2","M4","L3","L1","L4","W","M4","L5","L2","L4","M5",
                "L3","L4","L1","M5","L4","M3","L5","L2","L3","L1","L4","M3","L1","L3","M4","M1","M1","M1","M1","L1",
                "L3","L2","L4","M3","L2","L1","L5","M4","L1","L3","L4","M5","L5","L4","M4","L5","L2","L3","L4","L2",
                "L3","L1","L4","L3","L1","L2","L4","L3","L2","W","L1","L3","M4","M5","L2","L5","L4","M5","L2","L4",
                "L3","M5","L1","L3","L2","L5","L1","L3","L2","W","L5","M5","L1","L5","L2","M5","W","L2","L4","M4",
                "M1","M1","M1","M1","L1","L3","L4","L1","M4","L5","L2","L3","L1","M4","L5","W","L2","M3","L3","L4",
                "L2","M4","L3","L2"],

            ["L4","L3","M4","M5","L4","L3","L4","W","L1","L3","W","L4","L2","M4","L1","L2","M3","L1","L4","L2",
                "M3","L1","L3","M4","W","L5","L2","L1","M3","L4","M4","L2","L1","M3","L4","L3","L5","M3","L4","W",
                "L2","L4","L5","L2","L4","M3","L3","L5","L4","L3","L1","L4","M2","L3","L4","W","L5","L1","L3","M4",
                "W","L4","M3","L3","L3","L5","L4","L2","M5","L4","M3","L2","L4","M3","L2","L4","L1","M3","L4","M5",
                "M1","M1","M1","M1","L2","L5","L1","L4","M3","L3","L2","M4","W","M5","L3","W","L2","M2","L3","L1",
                "L4","L3","L3","L4","M1","M1","M1","M1","M2","L3","M4","M3","L2","L3","L4","M3","L5","L2","L3","L1",
                "W","M5","L4","L3","M4","L5","L2","L4","L3","L1","M2","L2","L4","L3","L5","L4","L2","L5","L4","L1",
                "M4","L3","L2","L4","M3","L1","L5","L3","L1","L4","W","M4","L3","L4","L1","L2","L4","M4","L3","L5",
                "M5","M4","L4","M3","L3","L2","L4","L3","M1","M1","M1","M1","L2","L1","L4","M3","L2","L1","L3","L4",
                "W","L2","M5","L3","L4","M4","L1","L4","L3","L5","L2","L4","M1","M1","M1","M1","L3","M4","L4","M2",
                "L2","L3","M5","M3","L3","W","L5","L4","L3","L1","L4","W","M2","L2","L3","L4"]
        ],

        "FREESPIN_MULTIPLIER" : [

            ["L3","W","M2","M4","L5","M5","L3","M2","M4","L1","L5","L3","M2","L2","M3","M3","M5","L5","M3","L3",
                "M5","L1","L5","L3","M4","L5","W","L3","L5","L2","M1","M1","M1","M1","L3","L5","W","M4","M5","L5",
                "M2","L1","L4","L3","M3","L1","L3","L4","M4","M5","L2","L5","L1","L3","M1","M1","M1","M1","M4","L4",
                "L1","L3","M3","M5","L3","M4","M2","L3","L1","W","L4","L3","M2","L5","M5","L1","L5","L3","M3","M4",
                "L1","L5","M3","M5","L1","M4","L4","W","M2","M5","M4","L3","M2","M5","L3","L4","L1","M5","M1","M1",
                "M1","M1","L4","L5","M4","L3","L2","M2","L3","W","M5","L5","L3","M4","L5","W","L2","L1","M4","L5",
                "L2","M3","M1","M1","M1","M1","L4","L1","M5","L5","M5","L1","M2","M3","L1","L4","M5","L3","W","L4",
                "L3","M3","M5","M4","L3","L1","M5","L3","W","L5","L3","M2","L1","M5","M2","L1","L4","L3","L5","L1",
                "M5","M3","L4","W","L3","L1","L5","L3","W","L5","L4","M5","L3","L2","M2","L4","L1","M5","L3","M4",
                "M3","L3","L1","M5","L3","L5","M1","M1","M1","M1","L2","L3","L4","W","L3","M5","L4","L3","L1","M5",
                "M2","L5","L3","M3","M5","L3","M2","L2","M4","L3","M2","L5","L4","W","M2","L5","M5","L1","L3","L5"],

            ["L2","M4","M5","W","L4","M4","L2","M3","L4","M2","L2","M4","M5","L3","M2","L5","M5","L2","L4","W",
                "L1","L3","L5","M5","L2","W","L3","M2","M3","L2","M1","M1","M1","M1","L4","L3","M3","L2","L1","W",
                "M2","M5","L3","L2","L5","L4","M2","M3","M4","L2","L3","L4","L1","W","L4","M5","L2","L5","M2","M4",
                "L5","L4","L2","M5","L4","W","L3","L2","L4","M5","L2","L4","L1","L2","L4","L3","M3","L3","M5","M2",
                "W","L2","L5","M5","M1","M1","M1","M1","M5","L5","L4","M5","M4","L1","M3","M5","M2","L4","L4","L2",
                "M3","L5","W","L2","M3","L5","L4","L3","M1","M1","M1","M1","M3","M5","L4","M4","M5","L3","M4","L2",
                "L5","W","L2","L3","L4","M5","L2","L4","L1","M2","M5","L4","M1","M1","M1","M1","M3","L5","M5","W",
                "L2","M2","L4","M5","L2","L1","M2","L2","W","L5","M3","M5","L2","M4","L4","M5","L2","L3","L4","W",
                "L5","M4","L2","M5","L4","W","L2","L3","W","L2","M3","M5","L1","L2","L4","M5","L2","M3","L5","L4",
                "M5","L2","W","M2","L2","M5","L4","M1","M1","M1","M1","M4","L1","L2","L4","M5","M3","L4","M5","W",
                "L4","L1","M3","M5","L4","M2","M3","L5","M5","L3","L1","M1","M1","M1","M1","L4","M5","M4","L4","M2",
                "L5","M4","W","L3","M5"],

            ["L2","M4","M5","L2","L3","M4","M5","L1","M2","L3","L5","M3","M4","L4","M5","W","L3","M3","M5","L1",
                "M1","M1","M1","M1","L4","L3","L2","M5","L5","W","L2","L4","M2","M5","L1","W","L3","W","L2","M5",
                "M4","L3","L4","L2","L1","L5","L4","M4","M3","L4","W","M2","M3","L1","M1","M1","M1","M1","L4","L2",
                "L3","M5","L4","M2","M4","W","L4","L5","M1","M1","M1","M1","L1","M2","L2","L4","M5","L3","M4","L5",
                "L3","M5","L2","M5","L1","L2","L3","W","L4","L3","W","L4","L3","L5","L2","W","M4","L5","L4","M2",
                "M5","L2","L1","L3","M4","L2","W","M5","M3","M2","L2","L1","L3","L5","M5","L3","L4","M4","W","L4",
                "M5","L3","L2","M3","L4","M5","L5","L3","M4","M2","W","M5","L2","L4","M4","L1","L2","W","L1","M4",
                "L2","M5","L5","M1","M1","M1","M1","M5","L1","L2","W","M4","M5","L5","L3","L1","L2","W","M2","L1",
                "M3","L3","M5","L5","L3","M3","L2","L3","M1","M1","M1","M1","M2","M4","L5","L1","W","L4","M4","L1",
                "M5","L2","L5","M5","W","M3","L5","M4","M2","M2","M3","L5","M5","M4","L3","M5","M2","L3","W","L2",
                "L4","M5","L2","L1","M5","M3","W","M4","L4","M5","L2","L5","M1","M1","M1","M1","L4","M4","M2","L1",
                "L5","W","M5","L5","L1","L4"],

            ["M4","L4","L5","W","L4","M4","M2","L2","M3","L1","L4","L3","M5","L5","M4","L1","L2","M3","M5","L1",
                "M1","M1","M1","M1","L3","L4","M4","L2","W","M5","M4","M2","L4","L2","L3","M3","L1","L4","M3","M2",
                "L1","L4","W","L2","W","M5","L3","M4","L1","L3","W","M3","L3","M5","L4","M1","M1","M1","M1","L3",
                "L2","L4","M5","W","M4","M2","M5","L3","L4","L2","M4","L4","W","L5","M2","L4","M5","L3","M5","L2",
                "M4","L3","M5","M4","L3","W","M3","L5","L3","M1","M1","M1","M1","L1","L2","M4","W","M5","L3","M2",
                "M4","L3","M5","M4","L4","L3","M5","L4","M2","M4","L3","L1","L4","W","M4","L5","L2","L4","M5","L3",
                "W","L1","M5","L4","M3","L5","L2","L3","M5","L4","M3","W","L3","M4","M1","M1","M1","M1","L1","M2",
                "M4","L4","M3","W","L1","M5","M4","L1","L3","L4","M5","L5","L4","M4","L5","L2","M2","L4","L2","L3",
                "M3","L4","M5","W","M2","M4","L3","M3","W","L1","L3","M4","M5","L2","M2","L4","M5","L2","L4","L3",
                "M5","W","L3","L2","L5","L1","L3","L2","W","L5","M5","L1","L5","L2","M5","W","L2","L4","M4","M1",
                "M1","M1","M1","L1","L3","L4","L1","M4","M2","M5","L3","L1","M4","L5","W","L2","M3","L3","M2","L2",
                "M4","L3","L2"],

            ["L4","L3","M4","M5","L4","M3","L4","W","L1","L3","W","L4","L2","M4","L1","L2","M3","L1","M2","L2",
                "M3","L1","L3","M4","W","L5","W","L1","M3","L4","M4","L2","W","M3","M5","L3","M4","M3","L4","W",
                "L2","L4","M4","W","M5","M3","M2","L5","M5","L3","L1","L4","M2","L3","M5","W","L5","M2","L3","M4",
                "W","L4","M3","L3","M4","M4","W","L2","M5","L4","M3","L2","M2","M3","L2","W","L1","M3","L4","M5",
                "M1","M1","M1","M1","L2","L5","L1","W","M3","L3","L2","M4","W","M5","L3","W","L2","M2","L3","L1",
                "M5","M5","W","L4","M1","M1","M1","M1","M2","L3","M4","M3","L2","W","M2","M3","L5","M5","L3","L1",
                "W","M5","L4","M2","M4","L5","L2","L4","L3","M3","M2","L2","M5","L3","M3","L4","W","L5","L4","L1",
                "M4","L3","M2","W","M3","M5","L5","L3","L1","L4","W","M4","L3","M5","L1","M2","L4","M4","L3","L5",
                "M5","M4","L4","M3","L3","W","M4","L3","M1","M1","M1","M1","L2","L1","W","M3","L2","W","L3","L4",
                "W","L2","M5","L3","L4","M4","W","M5","L3","L5","M2","L4","M1","M1","M1","M1","L3","M4","W","M2",
                "L2","L3","M5","M3","L3","W","L5","M3","M3","M5","L4","W","M2","L2","L3","L4"]
        ],

        "FREESPIN_COMBINED" : [
            ["L3","L2","L1","M5","L5","L1","L3","M2","L2","L1","L5","L3","L1","L2","L4","L1","L3","M3","L1","L3",
                "M5","L1","L5","L3","M4","L5","L1","M5","L5","L2","L1","L4","L5","L1","L3","L5","L1","L3","L4","L5",
                "L3","L1","L4","L3","W","M3","L1","L3","L4","L1","M5","L2","L5","L1","L3","M1","M1","M1","M1","M4",
                "L4","L1","L3","L5","L4","M5","L1","L4","L3","L1","W","L4","L3","L1","L5","L4","L1","L5","L3","M3",
                "M4","L1","M5","L4","L2","L1","M4","L4","L3","M2","L1","L4","M5","L1","M5","L3","L4","L1","M5","L4",
                "M5","L1","L3","L2","L1","L3","L5","M5","L5","L3","M4","L5","W","L2","M4","L3","L5","M4","L3","M1",
                "M1","M1","M1","L4","M4","L3","L5","L4","L1","M5","M3","L1","L4","M5","L3","M5","L4","L3","L1","M4",
                "L4","L3","L1","M5","L3","W","L5","L3","L2","M5","L3","L5","M5","L4","L3","M5","L1","M5","M3","L4",
                "L5","L3","L1","L5","L3","M4","L5","L4","M5","L3","L2","L5","L4","L1","M5","L3","L1","M3","L3","M4",
                "L2","M3","L5","M1","M1","M1","M1","L2","L3","L4","L5","L3","M5","L4","L3","L1","M4","M2","L5","L3",
                "M3","M4","L3","M2","L2","M5","L3","M2","L5","L4","L1","L3","L5","M5","L1","L3","L5","M1","M1","M1",
                "M1","L3","L2","L1","M5","L5","L1","L3","M2","L2","L1","L5","L3","L1","L2","L4","L1","L3","M3","L1",
                "L3","M5","L1","L5","L3","M4","L5","L1","M5","L5","L2","L1","L4","L5","L1","L3","L5","L1","L3","L4",
                "L5","L3","L1","L4","L3","M3","L1","L3","L4","L1","M5","L2","L5","L1","L3","M1","M1","M1","M1","M4",
                "L4","L1","L3","L5","L4","M5","L1","L4","L3","L1","W","L4","L3","L1","L5","L4","L1","L5","L3","M3",
                "M4","L1","M5","L4","L2","L1","M4","L4","L3","M2","L1","L4","M5","L1","M5","L3","L4","L1","M5","L4",
                "M5","L1","L3","L2","L1","L3","L5","M5","L5","L3","M4","L5","W","L2","M4","L3","L5","M4","L3","L4",
                "M4","L3","L5","L4","L1","M5","M3","L1","L4","M5","L3","M5","L4","L3","L1","M4","L4","L3","L1","M5",
                "L3","W","L5","L3","L2","M5","L3","L5","M5","L4","L3","M5","L1","M5","M3","L4","L5","L3","L1","L5",
                "L3","M4","L5","L4","M5","L3","L2","L5","L4","L1","M5","L3","L1","M3","L3","M4","L2","M3","L5","M1",
                "M1","M1","M1","L2","L3","L4","L5","L3","M5","L4","L3","L1","M4","M2","L5","L3","M3","M4","L3","M2",
                "L2","M5","L3","M2","L5","L4","L1","L3","L5","M5","L1","L3","L5"],

            ["L2","L5","M5","L2","M4","L5","L2","M3","L4","M2","L2","L4","M5","L3","L2","L5","L1","L2","L4","L5",
                "L1","L3","L5","M5","L2","L4","M4","M2","M3","L2","M1","M1","M1","M1","L4","L3","M3","L2","M5","L4",
                "M2","L1","L3","L2","L5","L4","L2","M3","L4","L2","M3","L4","L1","L4","L4","M5","L2","L5","L4","L2",
                "L5","L4","L2","M5","L4","W","L3","L2","L4","M5","L2","L4","L1","L2","L4","L3","M3","L1","L2","L3",
                "M4","L2","L5","M5","M1","M1","M1","M1","L2","L5","L4","M5","M4","L1","L2","M4","L1","L5","L4","M5",
                "L1","L5","L2","L2","M3","L5","M5","L3","M3","L5","L4","M4","M5","L3","M4","L2","L5","W","L2","L3",
                "L4","M5","L2","M3","L1","M2","M5","L4","M1","M1","M1","M1","M3","L5","M4","L2","L2","M2","L4","M5",
                "L3","L1","M2","L2","W","L5","M3","M5","L2","L1","L4","M5","L2","L3","L4","L3","L5","M4","L2","M5",
                "L4","L2","L3","M5","L2","M3","L4","L1","L2","L4","M5","L2","M3","L5","L4","M5","L2","M4","L4","L2",
                "M5","L4","M4","L1","L2","L4","M5","M3","L4","M5","W","L4","L1","L2","M5","L4","L2","M3","L5","M5",
                "L3","M4","M1","M1","M1","M1","L4","M5","M4","L4","M3","L5","M4","L2","L3","M5"],

            ["L2","M4","M5","L2","L3","L5","L4","L1","L2","L3","L5","L1","M4","L4","M5","W","L3","L4","M5","L1",
                "M1","M1","M1","M1","L4","L3","L2","M5","L5","L1","L2","L4","M2","M5","L1","W","L3","L4","L2","M5",
                "M4","L3","L4","L2","L1","L5","L4","L1","M4","M3","L1","L3","L2","M4","L4","L2","L3","M5","L4","L5",
                "M4","L3","L4","L5","M1","M1","M1","M1","L1","M2","L2","L4","M5","L3","L4","M4","L3","M5","L2","L4",
                "L1","L2","L3","L1","L4","L3","W","L4","L3","L5","L2","L1","M4","W","L5","L4","M2","M5","L2","M3",
                "L3","M4","L2","L4","M5","M3","M2","M5","L1","L3","L5","M5","L3","L4","M4","L3","L4","L1","L3","L1",
                "W","L2","M4","L4","M5","L5","L3","L4","L1","M4","L2","L4","M4","L1","L2","W","L1","L3","L2","L1",
                "L5","M1","M1","M1","M1","M5","L1","L2","M5","L1","M5","L5","L3","L1","M4","M2","L1","M3","L3","L4",
                "L5","L3","L1","L2","L3","M1","M1","M1","M1","M2","M4","L5","M4","L3","L4","M4","L1","M5","L2","L5",
                "M5","L4","L1","L5","M4","M2","L1","L4","L5","M5","M4","L3","M5","L5","L3","L1","L2","L4","M5","L2",
                "L1","M5","L4","L2","M4","L4","L1","M4","L5","M1","M1","M1","M1","L4","M4","L2","L1","M4","L2","M5",
                "L5","L1","L4"],

            ["M4","L4","L5","L3","L4","L1","L3","L2","M3","L1","L4","L3","L2","L5","L3","L1","L2","M3","M5","L1",
                "M1","M1","M1","M1","L3","L4","M4","L2","L1","W","L2","L1","M4","L2","L3","M3","L1","L4","L3","L2",
                "L1","L4","L5","L2","M5","L3","M4","L1","L3","L2","M3","L3","M5","L4","M1","M1","M1","M1","L3","L2",
                "L4","M5","L5","M5","L2","M5","L3","L4","L2","M4","L4","L2","W","M4","L4","L1","L3","M5","L2","M4",
                "L3","M5","M4","L3","L4","M3","L5","L3","M1","M1","M1","M1","L1","L2","M4","L5","L4","W","L3","L1",
                "M4","L3","M5","M4","L4","L3","M5","M4","L2","M4","L3","L1","L4","M4","L5","L2","L4","M5","L3","L4",
                "L1","M5","L4","M3","L5","L2","L3","L1","L4","M3","L1","L3","M4","M1","M1","M1","M1","L1","M5","L2",
                "L4","M3","L2","L1","L5","M4","L1","L3","L4","M5","L5","L4","M4","L5","L2","L3","L4","L2","L3","L1",
                "L4","L3","L1","L2","L4","M4","L2","W","L1","L3","M4","M5","L2","L5","L4","M5","L2","L4","L3","M5",
                "L1","L3","L2","L5","L1","L3","L2","L5","M5","L1","L5","L2","M5","W","L2","L4","M4","M1","M1","M1",
                "M1","L1","L3","L4","L1","M4","L5","L2","L3","L1","M4","L5","W","L2","M3","L3","L4","L2","M4","L3",
                "L2"],
            ["L4","L3","M4","M5","L4","L3","L4","L1","L1","L3","W","L4","L2","M4","L1","L2","M3","L1","L4","L2",
                "M3","L1","L3","M4","L1","L5","L2","L1","M3","L4","M4","L2","L1","M3","L4","L2","L5","M3","L4","W",
                "L2","L4","L5","L2","L4","M3","L3","L4","L4","L3","L1","L4","M2","L3","L4","M5","W","L1","L3","M4",
                "L1","L4","M3","L1","L3","L5","L4","L2","M5","L4","M3","L2","L4","M3","L2","M4","L1","M3","L4","M5",
                "M1","M1","M1","M1","L2","L5","L1","L4","M3","L3","L2","M4","L2","M5","L3","L3","L2","M2","M5","L1",
                "L4","L2","L3","L4","M1","M1","M1","M1","M2","L3","M4","M3","L2","L3","L4","M3","L5","L2","L3","M4",
                "L4","M5","L4","L3","M4","L5","L2","L4","L3","L1","M2","L2","M4","L3","L5","L4","L2","L5","L4","L1",
                "M4","L3","L2","L4","M3","L1","L5","L3","L1","L4","L5","M4","L3","L4","L1","W","L4","M4","L3","L5",
                "M5","M4","L4","M3","L3","L2","L4","L3","L5","L5","L1","L4","L2","L1","L4","M3","L2","L1","L3","L4",
                "W","L2","M5","L3","L4","M4","L1","L4","L3","L5","L2","L4","M1","M1","M1","M1","L3","M4","L4","M2",
                "L2","L3","M5","M3","L3","L5","L5","L4","L3","L1","L4","W","M2","L2","L3","L4"]
        ],
    };

    WIN_RATIOS:IBuffaloWinRatios = {
        normalWin : [
            {
                ratio : 0,
                text : "",
                duration : 0,
                type : WinRatioType.TINY,
                sound: WinterHoardSoundConfig.TINY_WIN
            },
            {
                ratio : 1.5,
                text : "",
                duration : 0.15,
                type : WinRatioType.SMALL,
                sound: WinterHoardSoundConfig.SMALL_WIN
            },
            {
                ratio : 3,
                text : "",
                duration : 0.5,
                type : WinRatioType.MEDIUM,
                sound: WinterHoardSoundConfig.MEDIUM_WIN
            },
            {
                ratio : 5,
                text : "",
                duration : 1.5,
                type : WinRatioType.LARGE,
                sound: WinterHoardSoundConfig.LARGE_WIN
            }
        ],
        bigWin : [
            {
                ratio : 15,
                text : "WOW",
                duration : 7.6,
                type : WinRatioType.BIG_WIN,
                sound: WinterHoardSoundConfig.BIG_WIN
            },
            {
                ratio : 25,
                text : "AMAZING",
                duration : 7.45,
                type : WinRatioType.MEGA_WIN,
                sound: WinterHoardSoundConfig.MEGA_WIN

            },
            {
                ratio : 50,
                text : "SHOCKING",
                duration : 7.4,
                type : WinRatioType.SUPER_MEGA_WIN,
                sound: WinterHoardSoundConfig.SUPER_MEGA_WIN

            }
        ]
    };

    NEW_BET_WHEN_ABORT_WIN_PRESENTATIONS:boolean = false;

    /*    GAME_TWEAKER_CONFIG:IGameTweakerConfig = {
            layer : this.LAYERS.TWEAK_MODULE.name,
            baseWindowWidth : 400,
            moduleHeight : 32,
            copyAllButtonText : "Copy All",
            buttonSkinIdle : "button",
            buttonSkinOver : "button",
            buttonSkinDown : "button",
            buttonSkinDisabled : "button-disabled"
        };*/
    PERFORMANCE_LOGGER:boolean = true;

    // #287
    WIN_BELOW_STAKE: boolean = false;
    private constructor() {
        if(WinterHoardGameConfig.instance) {
            throw new Error("WinterHoardGameConfig.constructor() - Instantiation failed: Singleton.");
        }
    }
}